<script>
  import Button from '@leaflink/stash/Button.vue';
  import Expand from '@leaflink/stash/Expand.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Input from '@leaflink/stash/Input.vue';
  import Textarea from '@leaflink/stash/Textarea.vue';
  import { storeToRefs } from 'pinia';

  import { useShopStore } from '@/piniaStores/shop/shop.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import { trackSampleRequestSent } from '@/segment';

  export default {
    name: 'RequestSample',

    components: {
      Button,
      Expand,
      Icon,
      Input,
      Textarea,
    },

    props: {
      product: {
        type: Object,
        required: true,
      },

      /**
       * For Segment tracking purposes, indicates the context in which the modal was opened
       */
      source: {
        type: String,
        required: true,
      },
    },

    data() {
      return {
        errors: {},
        message: '',
        isOpen: false,
        quantity: 1,
      };
    },

    setup() {
      const shopStore = useShopStore();

      const { sendSampleRequest } = shopStore;

      const { userActiveCompany } = storeToRefs(useUserStore());

      return {
        activeCompany: userActiveCompany,
        sendSampleRequest,
      };
    },

    methods: {
      /**
       * Handles form submission
       * Creates a sample request for the selected product
       */
      async submit() {
        this.errors = {};

        try {
          await this.sendSampleRequest({
            productId: this.product.id,
            quantity: this.quantity,
            message: this.message,
          });
        } catch (error) {
          const { response } = error || {};

          // TODO: check specific error statuses instead of all non-500 errors
          if (response?.status < 500 && typeof response.data === 'object') {
            this.errors = response.data;
            return;
          } else {
            throw error;
          }
        }

        this.$notify.success(this.$t('shop.sampleRequestSuccess'));
        this.trackSampleRequested();
        this.close();
      },

      close() {
        this.errors = {};
        this.quantity = 1;
        this.message = '';
        this.isOpen = false;
      },

      handleInput() {
        this.errors = {};
      },

      trackSampleRequested() {
        trackSampleRequestSent({
          title: 'Sample Request Sent',
          buyer_id: this.activeCompany.id,
          buyer_name: this.activeCompany.name,
          buyer_group: this.activeCompany.group?.id,
          seller_id: this.product.brand.company.id,
          seller_name: this.product.brand.company.name,
          brand_id: this.product.brand.id,
          brand_name: this.product.brand.name,
          product_id: this.product.id,
          product_name: this.product.name,
          requested_quantity: this.quantity,
          source: this.source,
        });
      },
    },
  };
</script>

<template>
  <section class="border rounded">
    <header
      tabindex="0"
      :class="['flex align-middle cursor-pointer', $style.toggle]"
      @click="isOpen = !isOpen"
      @keydown.enter="isOpen = !isOpen"
    >
      <Button data-test="button|request-sample" tabindex="-1" :class="['flex', $style.icon]" icon>
        <Icon :class="[isOpen ? 'rotate-270' : 'rotate-180']" name="chevron-left" />
      </Button>
      <h4 :class="['flex-auto', $style.heading]">{{ $t('shop.requestSample') }}</h4>
    </header>
    <Expand :is-expanded="isOpen">
      <form data-test="form|request-sample" class="ll-pa-2" @submit.prevent="submit">
        <Input
          v-model="quantity"
          name="quantity"
          type="number"
          data-test="input|request-sample-quantity"
          :class="['ll-mb-3', $style.quantity]"
          :error-text="(errors.quantity || []).join('<br>')"
          :label="$t('common.quantity')"
          @update:model-value="handleInput"
        />
        <Textarea
          v-model="message"
          class="ll-mb-3"
          name="message"
          :error-text="(errors.message || []).join('<br>')"
          :label="$t('common.notes')"
        />
        <div :class="['ll-grid ll-grid-cols-2', $style.buttons]">
          <Button data-test="button|cancelSampleRequest" inline type="button" @click="close">
            {{ $t('common.cancel') }}
          </Button>
          <Button data-test="button|sendSampleRequest" primary type="submit">
            {{ $t('common.send') }}
          </Button>
        </div>
      </form>
    </Expand>
  </section>
</template>

<style lang="scss" module>
  .heading {
    color: var(--color-ice-700);
  }

  .icon {
    color: var(--color-ice-900);

    &:focus {
      box-shadow: none;
    }
  }

  .toggle:hover,
  .toggle:focus {
    outline: 0;

    .heading,
    .icon {
      color: var(--color-blue);
    }
  }

  .buttons {
    column-gap: space(2);
  }

  .quantity input {
    width: 80px;
  }
</style>
